import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Wallet, Building2, Globe } from 'lucide-react';

const Expertise = () => {
  const [expandedExpertise, setExpandedExpertise] = useState<number | null>(null);

  const toggleExpertise = (index: number) => {
    setExpandedExpertise(expandedExpertise === index ? null : index);
  };

  const expertiseAreas = [
    {
      icon: <TrendingUp size={40} className="text-accent" />,
      title: "Strategic Growth & Scaling",
      description: "From startups to global giants, we architect strategies that accelerate growth, optimize operations, and unlock new revenue streams. Whether it's scaling efficiently or restructuring for resilience, we're your right arm in achieving ambitious goals."
    },
    {
      icon: <Wallet size={40} className="text-accent" />,
      title: "Fintech, Payments & Blockchain",
      description: "With deep expertise in fintech innovation and blockchain technology, we guide organizations to harness disruptive tools, expand market presence, and lead in the digital economy—rooted in our extensive experience across the Middle East, Africa, Europe, Australasia, and the Americas."
    },
    {
      icon: <Building2 size={40} className="text-accent" />,
      title: "Corporate Development & Fundraising",
      description: "We empower businesses to secure capital, forge strategic partnerships, and refine corporate structures, ensuring you're positioned for sustainable success and maximum valuation in a competitive world."
    },
    {
      icon: <Globe size={40} className="text-accent" />,
      title: "Multi-Sector Technology Advisory",
      description: "Our cross-industry knowledge in AI, technology integration, and beyond enables us to deliver bespoke solutions that drive efficiency, innovation, and leadership—no matter your sector."
    }
  ];

  return (
    <section id="expertise" className="section-padding relative">
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-16">
          <h1 className="section-title mx-auto mt-0">Our Expertise</h1>
          <p className="text-base md:text-lg lg:text-xl text-neutral/80 mt-4 md:mt-6 max-w-3xl mx-auto px-4">
            We deliver transformative solutions across key areas that drive innovation and sustainable growth in today's dynamic markets.
          </p>
        </div>

        <div className={`flex flex-col lg:flex-row gap-6 lg:gap-8 relative max-w-7xl mx-auto min-h-[400px] lg:min-h-[500px] ${
          expandedExpertise !== null ? 'lg:items-center' : 'items-center justify-center'
        }`}>
          {/* Left side buttons */}
          <div className={`w-full lg:w-[480px] flex-shrink-0 flex flex-col space-y-4 mx-auto ${
            expandedExpertise !== null 
              ? 'lg:w-[320px] lg:translate-x-0' 
              : 'lg:translate-x-0'
          }`}>
            {expertiseAreas.map((area, index) => (
              <div
                key={index}
                className={`relative cursor-pointer rounded-lg overflow-hidden transition-all duration-300 ${
                  expandedExpertise === index
                    ? 'bg-[#111]/95 shadow-[0_0_20px_rgba(73,17,28,0.2)] border-2 border-accent' 
                    : 'bg-[#111]/90 hover:bg-[#111]/95 border-2 border-transparent hover:border-accent/50'
                }`}
                onClick={() => toggleExpertise(index)}
              >
                <div className="flex items-center p-4 md:p-5 space-x-4 relative group backdrop-blur-md">
                  <div className={`transition-colors duration-300 ${
                    expandedExpertise === index ? 'text-accent' : 'text-accent/70 group-hover:text-accent'
                  }`}>
                    {area.icon}
                  </div>
                  <h3 className={`text-lg md:text-xl font-semibold transition-colors duration-300 ${
                    expandedExpertise === index ? 'text-neutral' : 'text-neutral/90 group-hover:text-neutral'
                  }`}>
                    {area.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>

          {/* Right side content */}
          <AnimatePresence mode="wait">
            {expandedExpertise !== null && (
              <motion.div
                key={expandedExpertise}
                initial={{ opacity: 0, x: 100, scale: 0.95 }}
                animate={{ 
                  opacity: 1, 
                  x: 0, 
                  scale: 1,
                  transition: { 
                    duration: 0.5,
                    ease: [0.4, 0, 0.2, 1],
                    opacity: { duration: 0.3 },
                    scale: { duration: 0.4 }
                  }
                }}
                exit={{ 
                  opacity: 0, 
                  x: 100, 
                  scale: 0.95,
                  transition: {
                    duration: 0.3,
                    ease: [0.4, 0, 1, 1]
                  }
                }}
                className="flex-1 bg-[#111]/95 backdrop-blur-lg rounded-xl p-6 md:p-8 lg:p-10 border border-accent/20 shadow-[0_0_30px_rgba(73,17,28,0.15)]"
              >
                <div className="max-w-3xl">
                  <h2 className="text-2xl md:text-3xl font-bold text-neutral mb-4 md:mb-6 bg-gradient-to-r from-neutral to-neutral/90 bg-clip-text">
                    {expertiseAreas[expandedExpertise].title}
                  </h2>
                  <p className="text-sm md:text-base text-neutral/90 leading-relaxed">
                    {expertiseAreas[expandedExpertise].description}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Expertise;